/*
 * Copyright © 2022 EPO: European Patent Office.
 * ALL RIGHTS RESERVED
 */

import React from 'react';
import { Divider, Grid, Paper, Typography } from '@material-ui/core';
import { Element } from 'react-scroll';
import { makeStyles } from '@material-ui/core/styles';
import classNames from 'classnames';
import { isEmpty } from 'lodash';
import { UploadedAttachmentItem } from './UploadedAttachmentItem';

const useStyles = makeStyles(theme => ({
  section: {
    visibility: 'visible',
    opacity: 1,
    transition: 'visibility 0s, opacity 0.5s linear',
  },
  sectionHidden: {
    visibility: 'hidden',
    opacity: 0,
    height: 0,
    transition: 'visibility 0s, opacity 0.5s linear',
  },
  titleText: {
    color: theme.palette.titleText,
    fontSize: '1.25rem',
    fontWeight: 600,
  },
  whitePaper: {
    margin: '1.5rem 0',
    padding: '1rem 1rem',
    backgroundColor: theme.palette.white,
    boxShadow: '0px 2px 10px rgba(0, 0, 0, 0.1)',
    borderRadius: '1rem',

    [theme.breakpoints.up('md')]: {
      padding: '2rem 3rem',
    },
    [theme.breakpoints.up('xl2')]: {
      width: '75%',
      maxWidth: '60rem',
    },
  },
}));

/**
 * Component that lists the uploaded attachments for an attachment type.
 *
 * @param attachmentType - root attachment type
 * @param attachments - array with all the attachments of attachmentType
 * @param draftId - id of the current submission
 * @param parentClasses - classes from the hosting RJSF
 * @param onDeleteFile - handler for the removal of an attached item
 * @param readOnly - is form in read only mode
 * @param title - uploaded section title
 * @param isFieldHidden - flag used to hide the uploaded section
 * @returns {JSX.Element}
 * @constructor
 */
export default function UploadedAttachments({
  attachmentType,
  attachments,
  draftId,
  parentClasses,
  onDeleteFile,
  readOnly,
  title,
  isFieldHidden,
  schema,
}) {
  const classes = useStyles();
  const paperStyle = isEmpty(parentClasses?.whitePaper) ? classes?.whitePaper : parentClasses.whitePaper;

  return (
    <Element name={attachmentType} className={classNames(classes.section, isFieldHidden && classes.sectionHidden)}>
      {attachments && attachments.length > 0 && (
        <Paper className={paperStyle}>
          <Grid container spacing={1}>
            <Grid item xs={12} md={12}>
              <Typography data-testid={`${attachmentType}-title`} className={classes.titleText}>
                {title}
              </Typography>

              {attachments.map((attachment, index) => (
                <div
                  key={attachment.id}
                  id={`submission_attachment_${attachment.attachmentType?.toLowerCase()}_${attachment.attachmentSubType?.toLowerCase()}_${
                    attachment.id
                  }`}
                >
                  <UploadedAttachmentItem
                    draftId={draftId}
                    attachment={attachment}
                    onDeleteFile={onDeleteFile}
                    readOnly={readOnly}
                    schema={schema}
                  />
                  {index < attachments.length - 1 && <Divider variant="fullWidth" />}
                </div>
              ))}
            </Grid>
          </Grid>
        </Paper>
      )}
    </Element>
  );
}
